import { Status } from '../../statuses/types/status.types';

// Keep enum for backward compatibility with snapshotTable.applicationStatus
export enum VisaStatusEnum {
  PENDING = 'pending',
  PROCESSED = 'processed',
  FAILED = 'failed',
  IN_PROGRESS = 'in_progress',
}

export enum MaritalStatusEnum {
  SINGLE = 'single',
  MARRIED = 'married',
  DIVORCED = 'divorced',
  WIDOWED = 'widowed',
  SEPARATED = 'separated',
}

export enum FirstLanguageEnum {
  ENGLISH = 'en',
  ARABIC = 'ar',
  OTHER = 'other',
}

export enum GenderEnum {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

export enum PassportGenderEnum {
  M = 'm',
  F = 'f',
  O = 'o',
}

export enum NationalityEnum {
  UAE = 'UAE',
  INDIA = 'India',
  PAKISTAN = 'Pakistan',
  BANGLADESH = 'Bangladesh',
  OTHER = 'Other',
}

export enum WorkTypeEnum {
  FULL_TIME = 'full-time',
  PART_TIME = 'part-time',
  CONTRACT = 'contract',
  INTERNSHIP = 'internship',
  FREELANCE = 'freelance',
}

export enum ApplicationTypeEnum {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}

export enum TicketStatusEnum {
  OPEN = 'open',
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export enum ApprovalStatusEnum {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum EscalationLevelEnum {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum RootCauseCategoryEnum {
  TECHNICAL = 'technical',
  USER = 'user',
  PROCESS = 'process',
  OTHER = 'other',
}

export type ClientData = {
  clientName?: string;
  orgLevel1?: string;
  orgLevel2?: string;
  orgLevel3?: string;
  orgLevel4?: string;
  orgLevel5?: string;
  orgLevel6?: string;
  orgLevel7?: string;
  tradeLicenseNumber?: string;
  tradeLicenseIssueDate?: Date;
  tradeLicenseExpiryDate?: Date;
  estCardNumber?: string;
  estCardIssueDate?: Date;
  estCardExpiryDate?: Date;
  companyAddress?: string;
};

export type ApplicationData = {
  serviceName?: string;
  serviceStage?: string;
  serviceOutcome?: string;
  triggerTime?: Date;
  executionStartTime?: Date;
  executionEndTime?: Date;
  executionDuration?: number;
  documentNumber?: string;
  failureCategory?: string;
  failureDetail?: string;
  failureCount?: number;
  machineId?: string;
  incidentId?: string;
};

export type EmployeeData = {
  candidateId?: string;
  employeeId?: string;
  probotId?: string;
  employmentStartDateEstimate?: Date;
  employmentStartDateActual?: Date;
  employmentEndDate?: Date;
  addressHome?: string;
  addressLocal?: string;
  phoneHome?: string;
  educationLevel?: string;
  educationDescription?: string;
  maritalStatus?: MaritalStatusEnum;
  firstLanguage?: FirstLanguageEnum;
  numberOfChildren?: number;
  motherName?: string;
  fatherName?: string;
  typedFirstName?: string;
  typedMiddleName?: string;
  typedLastName?: string;
  typedDateOfBirth?: Date;
  typedGender?: GenderEnum;
  typedNationality?: NationalityEnum;
  biodataVerified?: boolean;
  companyDesignation?: string;
  mappedDesignation?: string;
  workType?: WorkTypeEnum;
  previousNationality?: NationalityEnum;
  askingForAddressInUae?: boolean;
  askingForMobileNumber?: boolean;
};

export type PassportData = {
  passportFirstName?: string;
  passportMiddleName?: string;
  passportLastName?: string;
  passportIssueDate?: Date;
  passportExpiryDate?: Date;
  passportDateOfBirth?: Date;
  passportGender?: PassportGenderEnum;
  passportNationality?: NationalityEnum;
  passportIssuingAuthority?: string;
  passportIssuingCountry?: NationalityEnum;
  passportPlaceOfBirth?: string;
};

export type EidData = {
  eidFirstName?: string;
  eidMiddleName?: string;
  eidLastName?: string;
  eidNumber?: string;
  eidIssueDate?: Date;
  eidExpiryDate?: Date;
  eidDateOfBirth?: Date;
  eidGender?: GenderEnum;
  eidNationality?: NationalityEnum;
  eidPlaceIssued?: string;
  eidOccupation?: string;
  eidCardNumber?: string;
};

export type VisaData = {
  visaEmirate?: string;
  visaDesignation?: string;
};

export type DriversLicenseData = {
  dlFullName?: string;
  dlCategory1?: string;
  dlCategory2?: string;
  dlNumber?: string;
  dlIssueDate?: Date;
  dlExpiryDate?: Date;
  dlDateOfBirth?: Date;
  dlPlaceIssued?: string;
};

export type ContractData = {
  noticePeriod?: number;
  probationPeriod?: number;
  restDays?: number;
  basicSalaryAmount?: number;
  basicSalaryFrequency?: string;
  housingAmount?: number;
  housingFrequency?: string;
  housingProvidedByCompany?: boolean;
  transportAllowanceAmount?: number;
  transportAllowanceFrequency?: string;
  otherAllowanceAmount?: number;
  otherAllowanceFrequency?: string;
  childrenSchoolAllowanceAmount?: number;
  telephoneAllowanceAmount?: number;
  telephoneAllowanceFrequency?: string;
  ticketAllowanceAmount?: number;
  ticketAllowanceFrequency?: string;
  commissionAmount?: number;
  commissionFrequency?: string;
};

export type RecruiterInfo = {
  recruiterName?: string;
  recruiterEmail?: string;
  recruiterTelephone?: string;
};

export type ReligionData = {
  religionCode?: string;
  religionName?: string;
  sectCode?: string;
  sectName?: string;
};

export type SnapshotTableData = {
  applicationStatus?: VisaStatusEnum;
  applicationType?: ApplicationTypeEnum;
  applicationCost?: number;
  createdTimestamp?: Date;
  statusTimestamp?: Date;
};

export type MappingData = {
  profCode?: string;
  profContract?: string;
  profGdrfa?: string;
  profGdrfaCode?: string;
  profHrms?: string;
  profPreferred?: string;
  profBu?: string;
  deptCode?: string;
};

export type FlightInfoData = {
  airlineName?: string;
  flightNumber?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  arrivalDate?: Date;
  arrivalTime?: string;
  bookingRef?: string;
  lastUpdated?: Date;
  updatedBy?: string;
};

export type TicketManagementData = {
  incidentNumber?: string;
  subject?: string;
  description?: string;
  requesterOrganisation?: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterPhone?: string;
  status?: TicketStatusEnum;
  probotId?: string;
  clientPriority?: string;
  probotPriority?: string;
  assignedTo?: string;
  relatedServiceCode?: string;
  resolutionSummary?: string;
  approvalStatus?: ApprovalStatusEnum;
  escalationLevel?: EscalationLevelEnum;
  attachments?: string;
  feedbackRating?: number;
  rootCauseCategory?: RootCauseCategoryEnum;
  linkedTickets?: string;
  automationFlag?: boolean;
  openTimestamp?: Date;
  lastUpdateTimestamp?: Date;
  closedTimestamp?: Date;
  timeToFirstResponse?: number;
  timeToResolution?: number;
};

export type VideoRecording = {
  id: string;
  dataSource?: string;
  dataId?: string;
  videoUrl?: string;
  isDeleted?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
};

export enum ErrorType {
  BOT_ERROR = 'bot_error',
  DATA_ERROR = 'data_error',
}

export type ErrorLogMeta = {
  errorType: ErrorType;
  step: string;
  fieldName?: string;
};

export type ErrorLog = {
  id: string;
  dataSource: string;
  dataId: string;
  errorMessage: string;
  meta?: ErrorLogMeta;
  isDeleted?: boolean;
  deletedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
};

export type VisaApplication = {
  id: string;
  mrz?: string;
  passportNumber?: string;
  applicationNumber?: string;
  visaType?: string;
  visaStatus: Status | string;
  visaUidNumber?: string;
  visaFileNumber?: string;
  visaIssueDate?: Date;
  visaExpiryDate?: Date;
  visaName?: string;
  visaSponsor?: string;

  clientData?: ClientData;
  applicationData?: ApplicationData;
  employeeData?: EmployeeData;
  passportData?: PassportData;
  eidData?: EidData;
  visaData?: VisaData;
  driversLicense?: DriversLicenseData;
  contract?: ContractData;
  recruiterInfo?: RecruiterInfo;
  religion?: ReligionData;
  snapshotTable?: SnapshotTableData;
  mapping?: MappingData;
  flightInfo?: FlightInfoData;
  ticketManagement?: TicketManagementData;
  videoRecordings?: VideoRecording[];
  errorLogs?: ErrorLog[];

  tenantId: string;
  isDeleted?: boolean;
  deletedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
};

export type VisaApplicationResponse = {
  id: string;
  mrz?: string;
  passportNumber?: string;
  applicationNumber?: string;
  visaType?: string;
  visaStatus: Status | string;
  visaUidNumber?: string;
  visaFileNumber?: string;
  visaIssueDate?: Date;
  visaExpiryDate?: Date;
  visaName?: string;
  visaSponsor?: string;
  clientData?: ClientData;
  applicationData?: ApplicationData;
  employeeData?: EmployeeData;
  passportData?: PassportData;
  eidData?: EidData;
  visaData?: VisaData;
  driversLicense?: DriversLicenseData;
  contract?: ContractData;
  recruiterInfo?: RecruiterInfo;
  religion?: ReligionData;
  snapshotTable?: SnapshotTableData;
  mapping?: MappingData;
  flightInfo?: FlightInfoData;
  ticketManagement?: TicketManagementData;
  videoRecordings?: VideoRecording[];
  errorLogs?: ErrorLog[];
  isDeleted?: boolean;
  deletedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
};

export type VisaApplicationCreatePayload = {
  mrz?: string;
  passportNumber?: string;
  applicationNumber?: string;
  visaType?: string;
  visaStatus: string;
  visaUidNumber?: string;
  visaFileNumber?: string;
  visaIssueDate?: Date;
  visaExpiryDate?: Date;
  visaName?: string;
  visaSponsor?: string;
  clientData?: ClientData;
  applicationData?: ApplicationData;
  employeeData?: EmployeeData;
  passportData?: PassportData;
  eidData?: EidData;
  visaData?: VisaData;
  driversLicense?: DriversLicenseData;
  contract?: ContractData;
  recruiterInfo?: RecruiterInfo;
  religion?: ReligionData;
  snapshotTable?: SnapshotTableData;
  mapping?: MappingData;
  flightInfo?: FlightInfoData;
  ticketManagement?: TicketManagementData;
  tenantId: string;
};

export type VisaApplicationUpdatePayload = {
  mrz?: string;
  passportNumber?: string;
  applicationNumber?: string;
  visaType?: string;
  visaStatus?: string;
  visaUidNumber?: string;
  visaFileNumber?: string;
  visaIssueDate?: Date;
  visaExpiryDate?: Date;
  visaName?: string;
  visaSponsor?: string;
  clientData?: ClientData;
  applicationData?: ApplicationData;
  employeeData?: EmployeeData;
  passportData?: PassportData;
  eidData?: EidData;
  visaData?: VisaData;
  driversLicense?: DriversLicenseData;
  contract?: ContractData;
  recruiterInfo?: RecruiterInfo;
  religion?: ReligionData;
  snapshotTable?: SnapshotTableData;
  mapping?: MappingData;
  flightInfo?: FlightInfoData;
  ticketManagement?: TicketManagementData;
};

export interface VisaApplicationExportRequest {
  email?: string;
  visaStatus: string;
}

export interface VisaApplicationExportResponse {
  data: {
    jobId: string;
  };
}
